import { getStatusChipStyle } from "@/lib/status-chip";
import type { SystemRecord } from "@/services/system-service";

// A connector's lifecycle is separate from its setup state (draft / needs
// auth). The persisted status controls whether it is available to users.
export type SystemLifecycleKey = "active" | "inactive";

export interface SystemLifecycle {
  key: SystemLifecycleKey;
  className: string;
  dotClassName: string;
}

export function isSystemInactive(
  system: Pick<SystemRecord, "status">,
): boolean {
  // Older rows can omit status, which historically meant active. Present an
  // archived row as inactive until its lifecycle is explicitly changed.
  return system.status === "inactive" || system.status === "archived";
}

const LIFECYCLE_STYLES: Record<
  SystemLifecycleKey,
  { pill: string; dot: string }
> = {
  active: {
    pill: [
      getStatusChipStyle("active").backgroundClassName,
      getStatusChipStyle("active").borderClassName,
      getStatusChipStyle("active").textClassName,
    ].join(" "),
    dot: getStatusChipStyle("active").dotClassName,
  },
  inactive: {
    pill: [
      getStatusChipStyle("inactive").backgroundClassName,
      getStatusChipStyle("inactive").borderClassName,
      getStatusChipStyle("inactive").textClassName,
    ].join(" "),
    dot: getStatusChipStyle("inactive").dotClassName,
  },
};

export function deriveSystemLifecycle(
  system: Pick<SystemRecord, "status">,
): SystemLifecycle {
  const key: SystemLifecycleKey = isSystemInactive(system)
    ? "inactive"
    : "active";

  return {
    key,
    className: LIFECYCLE_STYLES[key].pill,
    dotClassName: LIFECYCLE_STYLES[key].dot,
  };
}
